import type { PublicationModel } from "../../types/models";
import { isEditablePersistedPub, isFailedPub, isImmutablePub } from "./engine/status";
import { isPostNowTarget } from "./postNow";

// Post actions shared by both calendar hosts, with Edit post first.
// Published posts keep Analytics and View live in the top strip;
// Retry belongs beside the failure explanation.

export type CalendarPostMenuItemId =
  | "edit-post"
  | "edit-video"
  | "open-project"
  | "view-live"
  | "open-schedule"
  | "post-now"
  | "delete";

export type CalendarPostMenuItem = {
  id: CalendarPostMenuItemId;
  label: string;
  disabled?: boolean;
};

export type CalendarPostMenuScope = {
  /** The host names the destination for posts that can no longer be edited. */
  openScheduleLabel?: "Open schedule" | "Show in queue";
  /** The short behind the post: still loading, gone, or loaded. */
  videoState: "loading" | "missing" | "ready";
  /** The loaded short has a parent project to open. */
  hasProject: boolean;
  hasAccount: boolean;
  /**
   * The post has a row on the server. False only inside the scheduler, whose
   * calendar draws the PENDING plan: its tiles are preview rows keyed
   * `bulk-<videoId>-<accountId>` whose status is coerced to "scheduled" so the
   * tile can show a time — which makes `isEditablePersistedPub` true for a post
   * that has never been saved. "Post now" writes by publication id, so it is
   * the one item that must not believe that coerced status.
   */
  isPersisted: boolean;
  /**
   * How the bin item lands — drives its label. Inside `/schedule` every edit
   * is staged until Save, so the item reads "Remove from schedule" whether
   * the post is a never-saved draft or an already-booked row. On the global
   * calendar confirm deletes the row there and then, so it reads "Delete post".
   */
  removal: "staged" | "immediate";
};

/** Whether the inspector can fire Post now / Retry now for this post. */
export const canOfferCalendarPostNow = (
  publication: Pick<PublicationModel, "status" | "scheduledAt">,
  now: Date,
  scope: Pick<
    CalendarPostMenuScope,
    "isPersisted" | "hasAccount" | "videoState"
  >,
): boolean =>
  isEditablePersistedPub(publication) &&
  scope.isPersisted &&
  scope.hasAccount &&
  scope.videoState === "ready" &&
  isPostNowTarget(publication, now);

/** Recovery is offered only beside a failed post's explanation. */
export const canOfferCalendarRetry = (
  publication: Pick<PublicationModel, "status" | "scheduledAt">,
  now: Date,
  scope: Pick<
    CalendarPostMenuScope,
    "isPersisted" | "hasAccount" | "videoState"
  >,
): boolean =>
  isFailedPub(publication) && canOfferCalendarPostNow(publication, now, scope);

export const buildCalendarPostMenuItems = (
  publication: Pick<PublicationModel, "status" | "scheduledAt" | "publishedUrl">,
  now: Date,
  scope: CalendarPostMenuScope,
): CalendarPostMenuItem[] => {
  const items: CalendarPostMenuItem[] = [];
  const editable = isEditablePersistedPub(publication);
  const offerPostNow = canOfferCalendarPostNow(publication, now, scope);

  if (!isImmutablePub(publication) && scope.videoState !== "missing") {
    items.push({
      id: "edit-post",
      label: "Edit post",
      disabled: scope.videoState !== "ready",
    });
  }
  if (publication.publishedUrl && publication.status !== "published") {
    items.push({ id: "view-live", label: "View live" });
  }
  if (isImmutablePub(publication) && scope.videoState !== "missing") {
    items.push({
      id: "open-schedule",
      label: scope.openScheduleLabel ?? "Open schedule",
      disabled: scope.videoState !== "ready",
    });
  }

  // Edit video / Open project — leave the calendar for the short or its
  // project. Still loading, offered disabled; gone, withheld. Edit video
  // asks for no folder: the editor route resolves the short by itself.
  if (scope.videoState === "loading") {
    items.push({ id: "edit-video", label: "Edit video", disabled: true });
    items.push({ id: "open-project", label: "Open project", disabled: true });
  } else if (scope.videoState === "ready") {
    items.push({ id: "edit-video", label: "Edit video" });
    if (scope.hasProject) {
      items.push({ id: "open-project", label: "Open project" });
    }
  }
  // Retry has a single home beside the error. Only scheduled posts can
  // expose Post now here, and only when the canonical row can be submitted.
  if (offerPostNow && !isFailedPub(publication)) {
    items.push({ id: "post-now", label: "Post now" });
  }
  // No `destructive` flag to carry: the row is a plain tertiary item with a
  // bin on it, exactly like every other delete in the app's menus. The red it
  // used to wear was the only red menu row in the codebase — and, being
  // outranked in dark mode by PopoverPannel's tertiary colour rule, half the
  // time it was not even red. The LABEL follows how the click lands, not
  // whether the row already exists on the server.
  if (editable) {
    items.push({
      id: "delete",
      label:
        scope.removal === "staged" ? "Remove from schedule" : "Delete post",
    });
  }
  return items;
};
